import './Dashboard.css';
// import 'react-grid-layout/css/styles.css';

import { useGetAllUserMeta, useUpdateUserMeta } from "@api/userMetaService";
import MaestroLoader from "@components/MaestroLoader";
import { useMutation, useQuery } from "@tanstack/react-query";
import { authenticatedApi } from "api";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
// import GridLayout from "react-grid-layout";
import GridLayout, { Responsive, WidthProvider } from "react-grid-layout";
import Sheet from '@mui/joy/Sheet';
import DashboardWidget from '@components/widgets/DashboardWidget';
import { Button, ButtonGroup, IconButton, Stack, Switch, Typography, Tooltip } from '@mui/joy';
import DashboardWidthProvider from '@providers/DashboardWidthProvider';
import { useDocumentTitle, useWindowScroll, useWindowSize } from '@uidotdev/usehooks';
import Subheader from '@components/layout/Subheader';
import { useAuth } from '@providers/AuthProvider';
import { withSize } from 'react-sizeme'
import ConfigureWidget from '@components/widgets/ConfigureWidget';
import { v4 as uuidv4 } from 'uuid';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { useDashboard } from '@providers/DashboardProvider';
import { ManageDashboards } from '@components/widgets/ManageDashboards';
import { maestroLogo } from '@utils/assets';
import { debounce } from '@utils/debounce';
import { events } from '@utils/analytics';

const ResponsiveGridLayout = GridLayout;
// const ResponsiveGridLayout = WidthProvider(Responsive);
// const ResponsiveGridLayout = withSize()(Responsive);

function getTimeOfDayGreeting(name = '') {
  let data = [
    [0, 4, "Burning the midnight oil, %name%?"],
    [5, 11, "Good Morning, %name%!"],
    [12, 17, "Good Afternoon, %name%!"],
    [18, 24, "Good Evening, %name%!"],
  ];
  let hr = new Date().getHours();
  let greeting;

  for(var i = 0; i < data.length; i++){
      if(hr >= data[i][0] && hr <= data[i][1]) {
        greeting = data[i][2];
      }
  }

  return greeting.replace('%name%', name);
};

// Default storage for dashboards
const generateDefaultDashboards = ({user = null}) => ({
  '0': {
      name: 'Home',
      isPrimaryDashboard: true,
      widgets: [
          /* // Overview widget removed as a default in November 2025
          {
              "id": uuidv4(),
              "title": "Overview",
              "type": "misc",
              "sizeX": 3,
              "sizeY": 1,
              "options": {
                  "type": "overview"
              },
              "row": 0,
              "col": 0
          },
          */
          {
              "id": uuidv4(),
              "title": "Welcome to Maestro!",
              "type": "misc",
              "sizeX": 1,
              "sizeY": 2,
              "options": {
                  "type": "notes",
                  "text": "<p><b>Welcome to Maestro Community Manager!<\/b><\/p><p><br\/><\/p><p>Please be sure to check out our resources available at:<\/p><p>https:\/\/www.maestrocm.com\/support\/<\/p><p><br\/><\/p>"
              },
              "row": 1,
              "col": 0
          },
          {
              "id": uuidv4(),
              "title": "Work Plans",
              "type": "workplan",
              "sizeX": 2,
              "sizeY": 2,
              "options": {
                  "type": "tasklist",
                  "viewType": "contact",
                  "ids": [user.contact ? user.contact.id : null]
              },
              "row": 1,
              "col": 1
          }
      ]
  },
  '1': {
      name: 'Organization',
      widgets: []
  },
  '2': {
      name: 'Economic',
      widgets: []
  },
  '3': {
      name: 'Promotion',
      widgets: []
  },
  '4': {
      name: 'Design',
      widgets: []
  }
});

export default function Dashboard() {
    let { id = 0 } = useParams();
    const [hasDashboardMeta, setHasDashboardMeta] = useState(false);
    const [dashboardMetaId, setDashboardMetaId] = useState(null);
    const [dashboardData, setDashboardData] = useState(null);
    const [dashboardLayout, setDashboardLayout] = useState([]);
    const size = useWindowSize();
    const [scroll, scrollTo] = useWindowScroll();
    const {user} = useAuth();
    const {isEditing, setIsEditing, isConfiguringWidget} = useDashboard();

    useDocumentTitle(`${dashboardData?.meta_value?.name ?? 'Dashboard'} | Maestro Community Manager`);

    const {
        // isPending,
        isLoading,
        isError,
        error,
        data,
        isFetching,
        isPreviousData,
    } = useGetAllUserMeta();

    const updateDashboard = useUpdateUserMeta({id: dashboardMetaId});

    // there could be a LOT of updates made in a short period of time which would artifically inflate the
    // actual event of editing a dashboard we limit it to changes made within a 30 second period
    const debounceAnalyticsEvent = debounce(() => {
      events.objectEdited({type: 'dashboard'});
    }, 30000);

    const doUpdate = (metaValue) => {
      // todo: debounce this
      const action = updateDashboard.mutateAsync({
        meta_value: metaValue,
      });

      action.then(debounceAnalyticsEvent);

      return action;
    }

    const addDefaultDashboards = () => {
      let dashboardsToSave = [];
      const defaultDashboards = generateDefaultDashboards({user});
      for(const defaultDashboardId in defaultDashboards) {
        dashboardsToSave.push({
          meta_key: `dashboard_settings[${defaultDashboardId}]`,
          meta_value: defaultDashboards[defaultDashboardId],
        });
      }

      authenticatedApi.post('/user-meta', dashboardsToSave).then(() => {
        window.location.reload();
      });
    };

    const onRemove = (widget) => {
      widget.to_delete = true;
      updateWidget(widget);
    }

    const updateWidget = (widget) => {
      let metaValue = {...dashboardData.meta_value};
      let widgets = metaValue.widgets;

      let foundWidgetIndex = widgets.findIndex(row => (row.i === widget.i));

      // replace the widget
      if(foundWidgetIndex != -1) {
        // remove it
        if(widget?.to_delete === true) {
          metaValue.widgets.splice(foundWidgetIndex, 1);
        }
        // or update it
        else
        {
          metaValue.widgets[foundWidgetIndex] = widget;
        }
      }
      // or add it if it's new
      else
      {
        widget.i = uuidv4();
        // widget.w = 6;
        // widget.h = 2;
        widget.x = 0;

        // modify dimensions depending on the widget type

        // widget.y = 9999; // give an outrageously high value so that it corrects itself
        widget.y = 0; // default to top

        // calculate the Y pos based on the other widgets
        if(Array.isArray(metaValue.widgets) && metaValue.widgets.length) {
          // const calculateY =
          const highestYVal = metaValue.widgets.reduce((prev, current) => {
            return prev.y > current.y ? prev : current;
          });
          const highestHVal = metaValue.widgets.reduce((prev, current) => {
            return prev.h > current.h ? prev : current;
          });

          // default it to under the bottom-most section
          let y = highestYVal.y + highestYVal.h;

          // check if there's a super tall widget
          if((highestHVal.y + highestHVal.h) > y) {
            y = highestHVal.y + highestHVal.h;
          }

          widget.y = y;
        }

        // add the widget so we get immediate response
        setDashboardLayout([...dashboardLayout, widget]);
        setTimeout(() => {
          scrollTo(0, 99999);
        }, 100);

        metaValue.widgets.push(widget);
      }

      return doUpdate(metaValue);
    };

    useEffect(() => {
        if(!data) {
            return;
        }

        let _hasDashboardMeta = data.data && data.data.find(obj => obj?.meta_key?.indexOf('dashboard_settings') !== -1);
        setHasDashboardMeta(_hasDashboardMeta);

        if(!_hasDashboardMeta) {
          return;
        }

        let dataForCurrentDashboard = data.data.find(obj => obj.meta_key === `dashboard_settings[${id}]`);
        // todo: update API so it returns JSON
        try {
            dataForCurrentDashboard.meta_value = JSON.parse(dataForCurrentDashboard.meta_value);
        } catch(e) {
            //
        }
        console.log(dataForCurrentDashboard);
        setDashboardMetaId(dataForCurrentDashboard?.id);
        setDashboardData(dataForCurrentDashboard ?? {});

        let widgets = [...dataForCurrentDashboard?.meta_value?.widgets];
        widgets.map(widget => {
            // transform dashboard layout from gridster to react-grid-layout
            if(!widget.hasOwnProperty('w')) {
                widget.i = widget.id;
                widget.w = widget.sizeX * 2; // we have 6 columns instead of 3 now
                widget.h = widget.sizeY;
                widget.x = widget.col * 2; // we have 6 columns instead of 3 now
                widget.y = widget.row;
                delete widget.id;
                delete widget.sizeX;
                delete widget.sizeY;
                delete widget.col;
                delete widget.row;
            }

            // todo: save it

            // todo: enforce minimum sizes based on the widget type. e.g. stats should always be full width
        });

        setDashboardLayout(widgets);

    }, [data, id]);

    const onLayoutChange = (layout) => {
      // console.log(layout);
      if(!isEditing) {
        return;
      }

      // console.log(layout);

      let metaValue = {...dashboardData.meta_value};
      let widgets = metaValue.widgets;

      for(const widget of layout) {
        let foundWidgetIndex = widgets.findIndex(row => (row.i === widget.i));

        // replace the widget
        if(foundWidgetIndex != -1) {
          // console.log({...metaValue.widgets[foundWidgetIndex]});
          metaValue.widgets[foundWidgetIndex] = {
            ...metaValue.widgets[foundWidgetIndex],
            x: widget.x,
            y: widget.y,
            w: widget.w,
            h: widget.h,
          }
          // console.log({...metaValue.widgets[foundWidgetIndex]});
        }
      }

      doUpdate(metaValue);
    }

    if(isLoading) {
        return (<MaestroLoader centered/>)
    }

    if(!hasDashboardMeta) {
      return (<>
        <Stack alignItems={'center'} justifyContent={'center'} height={'100vh'} sx={{
          // marginLeft: '50%',
          // transform: 'translateX(-50%)',
          // alignSelf: 'center',
        }}>
          <Stack gap={4} alignItems={'center'}>
            <img
                src={maestroLogo}
                alt="Maestro Logo"
                style={{width: '300px', minWidth: '100px'}}
            />
            <Typography level="body-sm" textAlign={'center'}>
                {/* {isRouteErrorResponse(error) === true && errorForStatus(error?.status)} */}
                {/* {isRouteErrorResponse(error) !== true && errorForStatus(undefined)} */}
                {/* {errorForStatus(error?.status)} */}
                Your dashboards are not set up.
            </Typography>
            <Button onClick={addDefaultDashboards}>Add Default Dashboards</Button>
          </Stack>
        </Stack>
      </>)
    }

    if(dashboardMetaId === null) {
      return (<>
        <Stack alignItems={'center'} justifyContent={'center'} height={'100vh'} sx={{
        }}>
          <Stack gap={4} alignItems={'center'}>
            <img
                src={maestroLogo}
                alt="Maestro Logo"
                style={{width: '300px', minWidth: '100px'}}
            />
            <Typography level="body-sm" textAlign={'center'}>
                This dashboard does not exist.
            </Typography>
          </Stack>
        </Stack>
      </>)
    }



    if(!dashboardData) {
      return null;
    }

    return (<>
      <Subheader
        title={id === 0 ? getTimeOfDayGreeting(user?.first_name) : dashboardData.meta_value.name}
        // tint={'default'}
      >
        <ManageDashboards />

        <ConfigureWidget
          widget={null}
          updateWidget={updateWidget}
          isEditing={isEditing}
          activateButton={(btnProps) => (
            <Tooltip title="Add Widget">
              <IconButton
                variant='outlined'
                size='sm'
                onClick={btnProps.onClick}
                sx={{
                  transition: 'var(--mm-default-transition)',
                  ...(!isEditing ? {
                    opacity: 0,
                    pointerEvents: 'none',
                  } : {}),
                }}
              >
                <AddRoundedIcon/>
              </IconButton>
            </Tooltip>
          )}
        />

        <Typography
          component="label"
          startDecorator={
            <Switch
              // sx={{ ml: 1 }}
              checked={isEditing}
              onChange={(event) => setIsEditing(event.target.checked)}
            />
          }
        >
          Edit Dashboard
        </Typography>
      </Subheader>
      {dashboardLayout.length === 0 && (
        <Stack alignItems={'center'} justifyContent={'center'} height={'100vh'} sx={{
          // marginLeft: '50%',
          // transform: 'translateX(-50%)',
          // alignSelf: 'center',
        }}>
          <Stack gap={4} alignItems={'center'}>
            <img
                src={maestroLogo}
                alt="Maestro Logo"
                style={{width: '300px', minWidth: '100px'}}
            />
            <Typography level="body-sm" textAlign={'center'}>
                {/* {isRouteErrorResponse(error) === true && errorForStatus(error?.status)} */}
                {/* {isRouteErrorResponse(error) !== true && errorForStatus(undefined)} */}
                {/* {errorForStatus(error?.status)} */}
                This dashboard is empty.
            </Typography>
            <ConfigureWidget
              widget={null}
              updateWidget={updateWidget}
              isEditing={isEditing}
              activateButton={(btnProps) => <Button onClick={btnProps.onClick}>Add a Widget</Button>}
            />

          </Stack>
        </Stack>
      )}
      {dashboardLayout.length > 0 && (
        <ResponsiveGridLayout
          className="layout"
          layout={dashboardLayout}
          resizeHandles={['n', 'e', 's', 'w', 'nw', 'ne', 'se', 'sw']}
          // breakpoints={{ lg: 1000, md: 996, sm: 768, xs: 480, xxs: 0 }}
          // cols={{ lg: 6, md: 2, sm: 1, xs: 1, xxs: 1 }}
          margin={[16, 16]} // matches gap={2}
          cols={6}
          rowHeight={size.width >= 900 ? 160 : 200}
          width={size.width - (size.width >= 900 ? 70 : 0) - 16} // doc width - sidebar - padding
          isDraggable={isEditing && !isConfiguringWidget}
          isResizable={isEditing && !isConfiguringWidget}
          onLayoutChange={onLayoutChange}
          // useCSSTransforms={size.width >= 900}
        >
          {dashboardLayout.map(widget => (
            <div key={widget.i}>
              <DashboardWidget
                widget={widget}
                updateWidget={updateWidget}
                isEditing={isEditing && !isConfiguringWidget}
                onRemove={onRemove}
              />
            </div>
          ))}
        </ResponsiveGridLayout>
      )}
    </>);
}
